import { useState } from 'react'
import { router } from '@inertiajs/react'
import { Plus, LayoutGrid, Table as TableIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { ServerDataTable, type PagyMeta } from '@/pages/People/_components/ServerDataTable'
import { useSelectedRow } from '@/lib/useSelectedRow'
import type CrmFundraisingOpportunity from '@/types/serializers/CrmFundraisingOpportunity'
import { KanbanBoardComponent as KanbanBoard } from './_components/KanbanBoard'
import { opportunityColumns } from './_components/OpportunityColumns'
import { CreateOpportunitySheet } from './_components/CreateOpportunitySheet'
import { OpportunityDetailSheet } from './_components/OpportunityDetailSheet'

interface Props {
  opportunities: CrmFundraisingOpportunity[]
  pagy?: PagyMeta
  filters?: Record<string, string>
  view: 'table' | 'kanban'
}

export default function OpportunitiesIndex({
  opportunities,
  pagy,
  filters,
  view,
}: Props) {
  const [createOpen, setCreateOpen] = useState(false)
  const basePath = '/fundraising/opportunities'
  const { selected, select, clear } = useSelectedRow(basePath)
  const selectedId = selected ? Number(selected.id) : null

  function handleViewChange(newView: string) {
    router.get(
      '/fundraising/opportunities',
      { view: newView },
      { preserveState: true, preserveScroll: true }
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Opportunités</h1>
          <p className="text-muted-foreground">
            Pipeline de collecte de fonds et suivi des opportunités.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Nouvelle opportunité
        </Button>
      </div>

      <Tabs value={view} onValueChange={handleViewChange}>
        <TabsList>
          <TabsTrigger value="kanban">
            <LayoutGrid className="mr-1.5 h-4 w-4" />
            Kanban
          </TabsTrigger>
          <TabsTrigger value="table">
            <TableIcon className="mr-1.5 h-4 w-4" />
            Tableau
          </TabsTrigger>
        </TabsList>

        <TabsContent value="kanban">
          <KanbanBoard
            opportunities={opportunities}
            onCardClick={(id) => select(id)}
          />
        </TabsContent>

        <TabsContent value="table">
          {pagy ? (
            <ServerDataTable
              columns={opportunityColumns}
              data={opportunities}
              pagy={pagy}
              basePath={basePath}
              selectedId={selected?.id ?? null}
              onRowClick={(row) => select(row.id)}
            />
          ) : (
            <p className="text-sm text-muted-foreground py-8 text-center">
              Chargement...
            </p>
          )}
        </TabsContent>
      </Tabs>

      <OpportunityDetailSheet
        opportunityId={selectedId}
        onOpenChange={(open) => { if (!open) clear() }}
      />

      <CreateOpportunitySheet
        open={createOpen}
        onOpenChange={setCreateOpen}
      />
    </div>
  )
}
